import React, { useState } from "react";
import { intlShape } from 'react-intl';
import PropTypes from "prop-types";
import LocaleHoc from "../LocaleHoc";
import { StyledFlexWrapDiv, StyledCapLabel } from "./style";
import "./_capCondition.scss";
import ConditionString from "./ConditionString";
import CapCustomSelect from '../CapCustomSelect';
import CapMultiSelect from '../CapMultiSelect'
import ConditionNumber from "./ConditionNumber";
import CapLabel from "../CapLabel";
import CapSpin from "../CapSpin";
import messages from "./messages";
import { CONTAINS, DOUBLE, ENUM_TYPES, NUM_TYPES, STRING, ENUM, IN, REQUEST } from "./constants";

const ConditionCustomField = props => {
  const {
    intl: { formatMessage },
    multiSelectPlaceholder,
    treeData,
    isCustomField,
    customFieldConditions = {},
    setCustomFieldConditions,
    showCustomFieldDropDown,
    setShowCustomFieldDropDown,
    setConditionValidationError,
    conditionValidationError,
    fetchingDataStatus,
    possibleValuesData,
    handleExtendedFieldSelect,
    dataType,
    inMsg,
    getPossibleValuesStatus,
  } = props;
  const [selectedPossibleValue, setSelectedPossibleValue] = useState();
  const { CapLabelInline } = CapLabel;

  // selects the custom/extended field from the dropdown menu and renders the additional condition component
  // for that selected field based on the dataType and hides the dropdown
  const handleCustomExtFieldSelect = value => {
    // for custom field just the key needs to be set in the condtion as there is only default string type of custom fields
    isCustomField ? setCustomFieldConditions({
      ...customFieldConditions,
      key: value,
    }) : (
      treeData?.some((field) => {
        const { value: fieldValue, dataType: dataTypeFromField, id: fieldId } = field;
        if (fieldValue === value) {
          let conditionDataType = STRING;
          if (NUM_TYPES.includes(dataTypeFromField)) {
            conditionDataType = DOUBLE;
          } else if (ENUM_TYPES.includes(dataTypeFromField)) {
            conditionDataType = ENUM;
            !(value in possibleValuesData) && handleExtendedFieldSelect({fieldId, value});
          }
          setCustomFieldConditions({
            ...customFieldConditions,
            key: value,
            operator: [dataTypeFromField,dataType].includes(STRING) ? CONTAINS : null,
            dataType: conditionDataType,
          });
        }
      })
    );
    setShowCustomFieldDropDown(false);
  };

  //sets the values of operand and operator for the custom fields conditionString component
  const handleConditionExpressionChange = value => {
    setCustomFieldConditions(value);
  };

  const handlePossibleValueSelect = value => {
    setCustomFieldConditions({
      ...customFieldConditions,
      operator: IN, // IN operator is for the extended fields with enum data types
      operand: value,
    });
    setSelectedPossibleValue(value);
  }

  // in case of custom fields we have string dataType for the additional condition, 
  // in case of extended fields multiple datatypes are supported like strings and once mentioned in NUM_TYPES and ENUM_TYPES array
  const getConditionComponent = () => {
    if (customFieldConditions?.dataType === STRING || isCustomField) {
      return (
        <ConditionString
          conditionExpression={customFieldConditions}
          setConditionExpression={handleConditionExpressionChange}
        />
      );
    } else if (NUM_TYPES.includes(customFieldConditions?.dataType)) {
      return (
        <ConditionNumber
          conditionExpression={customFieldConditions}
          setConditionExpression={handleConditionExpressionChange}
          conditionValidationError={conditionValidationError}
          setConditionValidationError={setConditionValidationError}
        />
      );
    } else if (ENUM_TYPES.includes(customFieldConditions?.dataType)) {
      // for the extended fields whose data type is enum, 
      // eg. if key is 'efg' with possible values as [hello, world], below map
      // will return [{title: 'hello', key: 'hello}, {title: 'world', key: 'world}] 
      const possibleValuesTreeData = possibleValuesData[customFieldConditions?.key]?.map(
        possibleValue => ({title: possibleValue, key: possibleValue})
      );
      return (
        <StyledFlexWrapDiv className='extended-field-enum'>
          <CapSpin spinning={getPossibleValuesStatus === REQUEST}>
            <CapMultiSelect
              className='extended-field-tree'
              treeData={possibleValuesTreeData}
              appliedKeys={customFieldConditions?.operand || []}
              onSelect={handlePossibleValueSelect}
              placeholder={formatMessage(messages.selectPossibleValue)}
              searchKey={formatMessage(messages.title)}
              width="15.625rem"
              showSearch
              searchPlaceholder={formatMessage(messages.searchPossibleValue)}
              maxValuesToSelect={15}
              disableSelectAll
              showSelectButtonToolTip
              selectTooltipText={formatMessage(messages.maxPossibleValues)}
              isPossibleValues
            />
          </CapSpin>
        </StyledFlexWrapDiv>
      );
    }
  };

  return (
    <StyledFlexWrapDiv className="cap-condition-string">
      {showCustomFieldDropDown && 
        <StyledFlexWrapDiv className="cap-condition-multi-select cap-label-custom-field">
          <CapSpin spinning={fetchingDataStatus === REQUEST}>
          <CapCustomSelect
            className="custom-field-condition"
            popoverClassName="cap-condition-popover"
            value={customFieldConditions?.key}
            options={treeData}
            onChange={handleCustomExtFieldSelect}
            selectPlaceholder={multiSelectPlaceholder}
            searchKey={formatMessage(messages.title)}
            width="13.5rem"
            showSearch
          />
          </CapSpin>
        </StyledFlexWrapDiv>}
      {customFieldConditions.key && 
        <StyledFlexWrapDiv className="cap-label-custom-field">
          <StyledCapLabel type="label2">{customFieldConditions?.key}</StyledCapLabel>
            {getConditionComponent()}
        </StyledFlexWrapDiv>}
    </StyledFlexWrapDiv>
  );
};
ConditionCustomField.propTypes = {
  intl: intlShape.isRequired,
  multiSelectPlaceholder: PropTypes.string,
  treeData: PropTypes.object,
  customFieldConditions: PropTypes.any,
  setCustomFieldConditions: PropTypes.func,
  showCustomFieldDropDown: PropTypes.any,
  setShowCustomFieldDropDown: PropTypes.func,
  isCustomField: PropTypes.bool,
  setConditionValidationError: PropTypes.func,
  possibleValuesData: PropTypes.object,
  conditionValidationError: PropTypes.object,
  dataType: PropTypes.string,
  inMsg: PropTypes.string,
  fetchingDataStatus: PropTypes.string,
  getPossibleValuesStatus: PropTypes.string,
  handleExtendedFieldSelect: PropTypes.func,

};
export default LocaleHoc(ConditionCustomField, { key: "CapCondition" });
